/**
 * Pure clustering logic for the work-pattern import validation report (#426).
 *
 * Takes the flat row data produced by useWorkPatternImportInvocation and groups
 * rows into stable "issue-type" clusters so the UI can render them as collapsible
 * sections. Contains no React; safe to call from components and from tests.
 */
import type { WorkPatternRow } from "../../../generated/core-api";
import type {
  WorkPatternImportResult,
  WorkPatternImportParseFailedRow,
} from "../../hooks/WorkPatternImport/useWorkPatternImportInvocation";
import {
  areRedundantClientServerErrorMessages,
  isCycleDaysOutOfRangeErrorMessage,
  isDayHoursLengthMismatchErrorMessage,
  mergeClientAndServerRowErrors,
} from "./workPatternImportErrorMerge";

// ---------------------------------------------------------------------------
// Cluster identifiers and configuration
// ---------------------------------------------------------------------------

export type ClusterId =
  | "dayHours"
  | "duplicateInBatch"
  | "duplicateOnCompany"
  | "startDateBeforeCompany"
  | "startDateInvalid"
  | "parseSchema"
  | "catchAll"
  | "standardValues"
  | "passed";

export type ClusterKind = "error" | "warning" | "passed";

export interface ClusterConfig {
  id: ClusterId;
  kind: ClusterKind;
  /** Plain-language cluster heading shown in the report. */
  title: string;
  /** Fix hint shown below the cluster title (error/warning clusters only). */
  hint: string;
}

/** Ordered list of error clusters. Parse-schema errors are last so named error
 *  types are tried first; catch-all is the final fallback. */
const ERROR_CLUSTER_ORDER: ClusterId[] = [
  "dayHours",
  "duplicateInBatch",
  "duplicateOnCompany",
  "startDateBeforeCompany",
  "startDateInvalid",
  "parseSchema",
  "catchAll",
];

export const CLUSTER_CONFIGS: Record<ClusterId, ClusterConfig> = {
  dayHours: {
    id: "dayHours",
    kind: "error",
    title: "Day hours must be between 0 and 24",
    hint: "Set each day's hours from 0 to 24 in the file, then upload again.",
  },
  duplicateInBatch: {
    id: "duplicateInBatch",
    kind: "error",
    title: "Duplicate pattern name in the file",
    hint:
      "Each WorkPatternName may only appear once in this upload. Rename the duplicate row or remove it, then upload again.",
  },
  duplicateOnCompany: {
    id: "duplicateOnCompany",
    kind: "error",
    title: "Pattern name or code already exists on this company",
    hint:
      "Use a WorkPatternName and WorkPatternCode that are not already on this company. Import only creates new patterns — it does not overwrite existing ones.",
  },
  startDateBeforeCompany: {
    id: "startDateBeforeCompany",
    kind: "error",
    title: "Start date before company start",
    hint: "Set StartDate to the company start date or later.",
  },
  startDateInvalid: {
    id: "startDateInvalid",
    kind: "error",
    title: "Start date is missing or invalid",
    hint:
      "Enter StartDate in DD/MM/YYYY, DD/MM/YY, or YYYY-MM-DD format and check it is a real calendar date.",
  },
  parseSchema: {
    id: "parseSchema",
    kind: "error",
    title: "File format problem",
    hint: "Fix the file structure or column headers, then upload again.",
  },
  catchAll: {
    id: "catchAll",
    kind: "error",
    title: "Validation error",
    hint: "Fix the pattern in the file, then upload again.",
  },
  standardValues: {
    id: "standardValues",
    kind: "warning",
    title: "Standard hours or days don't match the day grid",
    hint: "Fix StandardHours / StandardDays in the file, or choose Ignore to keep the file values.",
  },
  passed: {
    id: "passed",
    kind: "passed",
    // Title and hint are computed dynamically by the report component
    // depending on whether blocking errors exist.
    title: "Passed",
    hint: "",
  },
};

// ---------------------------------------------------------------------------
// Row shape after clustering
// ---------------------------------------------------------------------------

export interface ClusteredRow {
  /** Index into `patterns[]`; -1 for parse-failed rows that have no WorkPatternRow. */
  patternIndex: number;
  csvRowNumber: number;
  workPatternName: string;
  clusterId: ClusterId;
  /** Server-returned or parse error message for error-cluster rows. */
  errorMessage?: string;
  /** Active mismatch warning messages for warning-cluster rows, or co-reported
   *  warnings on a blocking-error row (AC-19). */
  warningMessages?: string[];
  /** Undefined for parse-failed rows — WorkPatternCycleGrid cannot be rendered. */
  pattern?: WorkPatternRow;
}

export interface ClusterGroup {
  config: ClusterConfig;
  rows: ClusteredRow[];
}

// ---------------------------------------------------------------------------
// Tab types
// ---------------------------------------------------------------------------

export type TabId = "all" | "errors" | "warnings" | "passed";

export interface TabCounts {
  all: number;
  errors: number;
  warnings: number;
  passed: number;
}

// ---------------------------------------------------------------------------
// Error classification
// ---------------------------------------------------------------------------

function messageParts(message: string): string[] {
  return message
    .split(";")
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function isDayHoursLengthMismatchInMessage(message: string): boolean {
  return messageParts(message).some((part) =>
    isDayHoursLengthMismatchErrorMessage(part)
  );
}

function isCycleDaysOutOfRangeInMessage(message: string): boolean {
  return messageParts(message).some((part) =>
    isCycleDaysOutOfRangeErrorMessage(part)
  );
}

const MAX_IMPORT_CYCLE_DAYS = 365;

function declaredCycleDaysFromLengthMismatchPart(part: string): number | undefined {
  const clientMatch = part.match(/but CycleDays is (\d+)/i);
  if (clientMatch) {
    return Number(clientMatch[1]);
  }
  const serverMatch = part.match(/cycleDays \((\d+)\)/i);
  if (serverMatch) {
    return Number(serverMatch[1]);
  }
  return undefined;
}

/** Length/column mismatch caused by CycleDays outside 1–365 — bucket as validation, not file format (#486). */
function isDayHoursLengthMismatchDueToInvalidCycleDays(message: string): boolean {
  return messageParts(message).some((part) => {
    if (!isDayHoursLengthMismatchErrorMessage(part)) {
      return false;
    }
    const declared = declaredCycleDaysFromLengthMismatchPart(part);
    return (
      declared !== undefined &&
      (declared < 1 || declared > MAX_IMPORT_CYCLE_DAYS)
    );
  });
}

/** Non-numeric text in a DayN cell — not a 0–24 range violation. */
const DAY_CELL_NON_NUMERIC_RE = /Day\d+ contains invalid (?:number|value)/i;

/** Per-day 0–24 hour violations — not CycleDays 1–365 range. */
const DAY_HOURS_OUT_OF_RANGE_RE =
  /\b24 hours\b|over 24|day\d+.{0,20}hours|must be between 0 and 24|is out of range — must be between 0 and 24|dayHours values must be between/i;

const DUPLICATE_IN_BATCH_RE =
  /duplicate workpatternname/i;

const DUPLICATE_IN_BATCH_MARKER_RE = /first seen at/i;

const DUPLICATE_ON_COMPANY_RE = /already exists/i;

const START_DATE_BEFORE_COMPANY_RE =
  /before.*(company|start)|prior to|is before the company start date/i;

const START_DATE_FIELD_RE = /start.?date/i;

function classifyDuplicateCluster(message: string): ClusterId | null {
  for (const part of messageParts(message)) {
    if (
      DUPLICATE_IN_BATCH_RE.test(part) &&
      DUPLICATE_IN_BATCH_MARKER_RE.test(part)
    ) {
      return "duplicateInBatch";
    }
  }
  if (messageParts(message).some((part) => DUPLICATE_ON_COMPANY_RE.test(part))) {
    return "duplicateOnCompany";
  }
  return null;
}

function classifyStartDateCluster(message: string): ClusterId | null {
  for (const part of messageParts(message)) {
    if (START_DATE_BEFORE_COMPANY_RE.test(part)) {
      return "startDateBeforeCompany";
    }
  }
  if (messageParts(message).some((part) => START_DATE_FIELD_RE.test(part))) {
    return "startDateInvalid";
  }
  return null;
}

/** Classify a server- or client-returned error message into the most specific cluster. */
export function classifyErrorCluster(message: string): ClusterId {
  // CycleDays 1–365 — generic validation, not file format (#486).
  if (isCycleDaysOutOfRangeInMessage(message)) {
    return "catchAll";
  }
  if (isDayHoursLengthMismatchDueToInvalidCycleDays(message)) {
    return "catchAll";
  }
  // CycleDays vs Day-column / dayHours length — file structure, not per-day 0–24 (#474).
  if (isDayHoursLengthMismatchInMessage(message)) {
    return "parseSchema";
  }
  if (DAY_CELL_NON_NUMERIC_RE.test(message)) {
    return "catchAll";
  }
  // Day-hours: catches "Day3 is 26 hours", "must be between 0 and 24", etc.
  if (DAY_HOURS_OUT_OF_RANGE_RE.test(message)) {
    return "dayHours";
  }
  const duplicateCluster = classifyDuplicateCluster(message);
  if (duplicateCluster !== null) {
    return duplicateCluster;
  }
  const startDateCluster = classifyStartDateCluster(message);
  if (startDateCluster !== null) {
    return startDateCluster;
  }
  // Schema / column issues (EmployeeCode, CompanyCode, missing column)
  if (/EmployeeCode|CompanyCode|missing.*(column|header)|unexpected.*(column|header)|wrong.*(schema|format)/i.test(message)) {
    return "parseSchema";
  }
  return "catchAll";
}

// ---------------------------------------------------------------------------
// Main clustering function
// ---------------------------------------------------------------------------

export interface ClusteringInput {
  patterns: WorkPatternRow[];
  result: WorkPatternImportResult;
  warnings: Map<number, string[]> | undefined;
  ignoredWarningIndices: ReadonlySet<number>;
  /** Row-level client parse errors (e.g. duplicate name on a row that still parsed). */
  parseErrors?: string[];
  parseFailedRows: WorkPatternImportParseFailedRow[] | undefined;
  patternCsvRowNumbers: number[] | undefined;
}

const PARSE_ERROR_ROW_PREFIX = /^Row (\d+):\s*(.+)$/;

/** Map `Row N: …` parse errors onto pattern indices via CSV row numbers. */
export function clientParseErrorsByPatternIndex(
  parseErrors: string[] | undefined,
  patternCsvRowNumbers: number[] | undefined
): Map<number, string[]> {
  const csvRowToIndex = new Map<number, number>();
  patternCsvRowNumbers?.forEach((csvRow, index) => {
    csvRowToIndex.set(csvRow, index);
  });

  const byIndex = new Map<number, string[]>();
  for (const line of parseErrors ?? []) {
    const match = line.match(PARSE_ERROR_ROW_PREFIX);
    if (!match) continue;
    const csvRow = Number(match[1]);
    const message = match[2];
    const patternIndex = csvRowToIndex.get(csvRow);
    if (patternIndex === undefined) continue;
    const bucket = byIndex.get(patternIndex);
    if (bucket) {
      bucket.push(message);
    } else {
      byIndex.set(patternIndex, [message]);
    }
  }
  return byIndex;
}

/**
 * Cluster id for a row with both server and client parse errors.
 * Strips server parts that only repeat the client wording, then:
 * - if nothing distinct remains on the server, classify from the client;
 * - else if any client part is a CycleDays 1–365 range error, classify from the
 *   client (#486 — out-of-range CycleDays must not land under dayHours / file format
 *   even when the server also reports a different rule, e.g. duplicate-on-company);
 * - otherwise classify from the full server error string.
 * Displayed `errorMessage` is merged separately and is unchanged by this precedence.
 */
function classifyPrimaryErrorCluster(
  serverError: string | undefined,
  clientMessages: string[] | undefined
): ClusterId {
  const client =
    clientMessages !== undefined && clientMessages.length > 0
      ? clientMessages
      : undefined;

  if (serverError !== undefined && client !== undefined) {
    const serverParts = messageParts(serverError);
    const nonRedundantServer = serverParts.filter(
      (part) =>
        !client.some((clientPart) =>
          areRedundantClientServerErrorMessages(clientPart, part)
        )
    );
    if (nonRedundantServer.length === 0) {
      return classifyErrorCluster(client.join("; "));
    }
    if (client.some((part) => isCycleDaysOutOfRangeErrorMessage(part))) {
      return classifyErrorCluster(client.join("; "));
    }
  }

  if (serverError !== undefined) {
    return classifyErrorCluster(serverError);
  }
  if (client !== undefined) {
    return classifyErrorCluster(client.join("; "));
  }
  return "catchAll";
}

/**
 * Classify every row (parsed patterns + parse-failed rows) into a ClusteredRow.
 * The ordering within the returned array matches the source order.
 */
export function clusterRows(input: ClusteringInput): ClusteredRow[] {
  const {
    patterns,
    result,
    warnings,
    ignoredWarningIndices,
    parseErrors,
    parseFailedRows,
    patternCsvRowNumbers,
  } = input;

  // Build a map from pattern index to joined error string. Use a reduce rather
  // than a direct Map constructor so that multiple errors on the same index are
  // accumulated (joined with "; ") rather than silently overwriting each other.
  // Pacific can already emit several errors per row (e.g. negative standardHours
  // AND a dayHours length mismatch), and the merged-validate path can add a
  // Christmas error to the same row in the future.
  const errorByIndex = (result.errors ?? []).reduce<Map<number, string>>(
    (acc, e) => {
      const existing = acc.get(e.index);
      acc.set(e.index, existing !== undefined ? `${existing}; ${e.error}` : e.error);
      return acc;
    },
    new Map()
  );
  const clientErrorByIndex = clientParseErrorsByPatternIndex(
    parseErrors,
    patternCsvRowNumbers
  );

  const rows: ClusteredRow[] = [];

  // --- Parsed patterns ---
  patterns.forEach((pattern, index) => {
    const csvRowNumber = patternCsvRowNumbers?.[index] ?? index + 2;
    const serverError = errorByIndex.get(index);
    const clientMessages = clientErrorByIndex.get(index);
    const errorMessage = mergeClientAndServerRowErrors(
      serverError,
      clientMessages
    );
    const rowWarnings = warnings?.get(index);
    const hasActiveWarning =
      (rowWarnings?.length ?? 0) > 0 && !ignoredWarningIndices.has(index);

    if (errorMessage !== undefined) {
      // Error row — may also carry a co-reported warning (AC-19).
      rows.push({
        patternIndex: index,
        csvRowNumber,
        workPatternName: pattern.workPatternName,
        clusterId: classifyPrimaryErrorCluster(serverError, clientMessages),
        errorMessage,
        // Carry the warning alongside the error (shown in accordion, actions hidden by AC-19).
        warningMessages: hasActiveWarning ? rowWarnings : undefined,
        pattern,
      });
    } else if (hasActiveWarning) {
      rows.push({
        patternIndex: index,
        csvRowNumber,
        workPatternName: pattern.workPatternName,
        clusterId: "standardValues",
        warningMessages: rowWarnings,
        pattern,
      });
    } else {
      rows.push({
        patternIndex: index,
        csvRowNumber,
        workPatternName: pattern.workPatternName,
        clusterId: "passed",
        pattern,
      });
    }
  });

  // --- Parse-failed rows (no WorkPatternRow) ---
  (parseFailedRows ?? []).forEach((failed) => {
    rows.push({
      patternIndex: -1,
      csvRowNumber: failed.csvRowNumber,
      workPatternName: failed.workPatternName ?? `Row ${failed.csvRowNumber}`,
      clusterId: classifyErrorCluster(failed.error),
      errorMessage: failed.error,
      // No `pattern` — cycle grid cannot be rendered.
    });
  });

  return rows;
}

/**
 * Group a flat list of ClusteredRows into ClusterGroups, preserving the
 * ordering: errors (in ERROR_CLUSTER_ORDER) → warnings → passed.
 */
export function groupRowsByClusters(rows: ClusteredRow[]): ClusterGroup[] {
  const map = new Map<ClusterId, ClusteredRow[]>();
  for (const row of rows) {
    const bucket = map.get(row.clusterId);
    if (bucket) {
      bucket.push(row);
    } else {
      map.set(row.clusterId, [row]);
    }
  }

  const groups: ClusterGroup[] = [];

  // Error clusters in stable order
  for (const id of ERROR_CLUSTER_ORDER) {
    const clusterRows = map.get(id);
    if (clusterRows && clusterRows.length > 0) {
      groups.push({ config: CLUSTER_CONFIGS[id], rows: clusterRows });
    }
  }

  // Warning cluster
  const warnRows = map.get("standardValues");
  if (warnRows && warnRows.length > 0) {
    groups.push({ config: CLUSTER_CONFIGS["standardValues"], rows: warnRows });
  }

  // Passed cluster
  const passedRows = map.get("passed");
  if (passedRows && passedRows.length > 0) {
    groups.push({ config: CLUSTER_CONFIGS["passed"], rows: passedRows });
  }

  return groups;
}

/** Count rows by tab category. */
export function countTabs(rows: ClusteredRow[]): TabCounts {
  let errors = 0;
  let warnings = 0;
  let passed = 0;
  for (const row of rows) {
    const kind = CLUSTER_CONFIGS[row.clusterId].kind;
    if (kind === "error") errors++;
    else if (kind === "warning") warnings++;
    else passed++;
  }
  return { all: rows.length, errors, warnings, passed };
}

/** Filter groups for a given tab. */
export function getVisibleGroups(tab: TabId, groups: ClusterGroup[]): ClusterGroup[] {
  if (tab === "all") return groups;
  if (tab === "errors") return groups.filter((g) => g.config.kind === "error");
  if (tab === "warnings") return groups.filter((g) => g.config.kind === "warning");
  return groups.filter((g) => g.config.kind === "passed");
}

/** Return the first cluster id visible on a given tab, or null if none. */
export function firstClusterIdForTab(tab: TabId, groups: ClusterGroup[]): ClusterId | null {
  return getVisibleGroups(tab, groups)[0]?.config.id ?? null;
}

// ---------------------------------------------------------------------------
// Client-side name search helpers (#428)
// ---------------------------------------------------------------------------

/**
 * Returns true when `name` contains `term` as a case-insensitive substring.
 * An empty (or whitespace-only) term matches every name.
 */
export function matchesWorkPatternNameSearch(
  name: string,
  term: string
): boolean {
  const trimmed = term.trim();
  if (trimmed === "") return true;
  return name.toLowerCase().includes(trimmed.toLowerCase());
}

/**
 * Filters each group's rows by name search term, then drops groups that
 * become empty. Group order is preserved.
 */
export function filterGroupsByNameSearch(
  groups: ClusterGroup[],
  term: string
): ClusterGroup[] {
  if (term.trim() === "") return groups;
  return groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter((row) =>
        matchesWorkPatternNameSearch(row.workPatternName, term)
      ),
    }))
    .filter((group) => group.rows.length > 0);
}

// ---------------------------------------------------------------------------
// Banner text helpers (shared with WorkPatternImportTable via this module)
// ---------------------------------------------------------------------------

/**
 * Plain-language blocked banner copy (#426 portal copy table).
 *
 * Previously defined locally in WorkPatternImportTable; extracted here so both
 * the table and the new clustered report use a single source.
 */
export const WORK_PATTERN_CONFIRM_IMPORT_BANNER_ID =
  "work-pattern-confirm-import-banner";

export function formatBlockedImportBanner(
  errorCount: number,
  totalCount: number,
  importableCount: number
): string {
  const totalLabel = totalCount === 1 ? "pattern" : "patterns";
  const errorPhrase = errorCount === 1 ? "has an error" : "have errors";
  const main = `${errorCount} of ${totalCount} ${totalLabel} ${errorPhrase}.`;

  if (importableCount === 0) {
    return `${main} Patterns with errors can't be imported — fix the file and upload again.`;
  }

  const errorLabel = errorCount === 1 ? "pattern" : "patterns";
  const importableLabel = importableCount === 1 ? "pattern" : "patterns";
  return `${main} ${importableCount} ${importableLabel} can still be imported; ${errorCount} ${errorLabel} with errors will be skipped.`;
}

